import { createMD5 } from "hash-wasm";
import type { IHasher } from "hash-wasm/dist/lib/WASMInterface";

// Forked from https://github.com/shuding/stable-hash
// Created by Shu Ding. Released under the MIT License.

// Use WeakMap to store the object-key mapping so the objects can still be
// garbage collected. WeakMap uses a hashtable under the hood, so the lookup
// complexity is almost O(1).
const table = new WeakMap<object, string>();

// A counter of the key.
let counter = 0;

// A stable hash implementation that supports:
//  - Fast and ensures unique hash properties
//  - Handles unserializable values
//  - Handles object key ordering
//  - Generates fixed length results
//
// This is not a serialization function, and the result is not guaranteed to be
// parsable.
export default async function stableFixedLengthHash(arg: any): Promise<string> {
  await setupStableFixedLengthHasher();
  return stableFixedLengthHashSync(arg);
}

/**
 * The same as stableFixedLengthHash, but requires first calling setupStableFixedLengthHasher
 * Use this if you expect to call the function in rapid succession (eg in a for loop) as it
 * allows you to avoid the substantial overhead of promise invocation.
 */
export function stableFixedLengthHashSync(arg: any): string {
  const type = typeof arg;
  const constructor = arg && arg.constructor;
  const isDate = constructor === Date;

  if (Object(arg) === arg && !isDate && constructor !== RegExp) {
    // Object/function, not null/date/regexp. Use WeakMap to store the id first.
    // If it's already hashed, directly return the result.
    let result = table.get(arg);
    if (result) {
      return result;
    }

    // Store the hash first for circular reference detection before entering the
    // recursive `stableHash` calls.
    // For other objects like set and map, we use this id directly as the hash.
    result = ++counter + "~";
    table.set(arg, result);
    let index: any;

    const name = constructor.name;
    if (name && name.substring(name.length - 5) === "Array") {
      // Array or TypedArray.
      result = "@";
      for (index = 0; index < arg.length; index++) {
        result += stableFixedLengthHashSync(arg[index]) + ",";
      }
    } else if (name === "Object") {
      // Object, sort keys.
      const keys = Object.keys(arg).sort();
      result = "#";
      for (const index of keys) {
        if (arg[index] !== undefined) {
          result += index + ":" + stableFixedLengthHashSync(arg[index]) + ",";
        }
      }
    }

    result = hashObject(result);
    table.set(arg, result);
    return result;
  }

  if (isDate) {
    return hashObject(arg.toJSON());
  }

  if (type === "symbol") {
    return hashObject(arg.toString());
  }

  return type === "string" ? hashObject(arg) : hashObject("" + arg);
}

let sha1: IHasher;
export async function setupStableFixedLengthHasher() {
  if (sha1) {
    return;
  }
  sha1 = await createMD5();
  sha1.init();
}

const encoder = new TextEncoder();

function hashObject(msg: string): string {
  if (!sha1) {
    console.assert(false, "This requires that you first call setupStableFixedLengthHasher");
  }
  sha1.init();
  sha1.update(encoder.encode(msg));
  return sha1.digest("hex");
}
